import type { Product } from '@pranala/shared'

// Static data imported directly
// Using dynamic import for JSON with assert
declare const __PRODUCTS_DATA__: Product[]

// Import raw JSON content
import productsJson from './products.json'

const allProducts = (productsJson as any[]).map(p => ({
  ...p,
  // Ensure dates are properly typed
  createdAt: p.createdAt ? new Date(p.createdAt) : null,
  updatedAt: p.updatedAt ? new Date(p.updatedAt) : null,
  publishedAt: p.publishedAt ? new Date(p.publishedAt) : null,
})) as Product[]

export function getFeaturedProducts(limit: number = 8): Product[] {
  return allProducts
    .filter((p: Product) => p.status === 'published')
    .sort((a: Product, b: Product) => {
      // Sort by createdAt descending (terbaru dulu)
      const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0
      const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0
      return dateB - dateA
    })
    .slice(0, limit)
}

export function getProductBySlug(slug: string): Product | undefined {
  return allProducts.find((p: Product) => p.slug === slug)
}

export function getProductById(id: number): Product | undefined {
  return allProducts.find((p: Product) => p.id === id)
}

export function getProducts(options: {
  category?: string
  search?: string
  page?: number
  limit?: number
  sortBy?: 'newest' | 'oldest' | 'price-asc' | 'price-desc'
} = {}) {
  let result = [...allProducts].filter((p: Product) => 
    p.status === 'published'
  )
  
  if (options.category) {
    result = result.filter((p: Product) => 
      p.categories?.some((c: any) => c.slug === options.category || c.name === options.category)
    )
  }
  
  if (options.search) {
    const search = options.search.toLowerCase()
    result = result.filter((p: Product) => 
      p.title.toLowerCase().includes(search) ||
      p.description?.toLowerCase().includes(search) ||
      p.excerpt?.toLowerCase().includes(search)
    )
  }
  
  // Sorting logic
  const sortBy = options.sortBy || 'newest'
  switch (sortBy) {
    case 'newest':
      result.sort((a: Product, b: Product) => {
        const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0
        const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0
        return dateB - dateA
      })
      break
    case 'oldest':
      result.sort((a: Product, b: Product) => {
        const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0
        const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0
        return dateA - dateB
      })
      break
    case 'price-asc':
      result.sort((a: Product, b: Product) => (a.price || 0) - (b.price || 0))
      break
    case 'price-desc':
      result.sort((a: Product, b: Product) => (b.price || 0) - (a.price || 0))
      break
  }
  
  const total = result.length
  const page = options.page || 1
  const limit = options.limit || 12
  const start = (page - 1) * limit
  
  return {
    products: result.slice(start, start + limit),
    total,
    page,
    limit,
    totalPages: Math.ceil(total / limit),
  }
}

export function getAllCategories(): string[] {
  const categories = new Set<string>()
  allProducts.forEach((p: Product) => {
    p.categories?.forEach((c: any) => {
      if (c.name) categories.add(c.name)
    })
  })
  return Array.from(categories)
}

export function getRelatedProducts(currentSlug: string, limit: number = 4): Product[] {
  const currentProduct = getProductBySlug(currentSlug)
  if (!currentProduct) return []
  
  return allProducts
    .filter((p: Product) => 
      p.slug !== currentSlug && 
      (p.status === 'published') &&
      p.categories?.some((c: any) => 
        currentProduct.categories?.some((cc: any) => cc.id === c.id)
      )
    )
    .slice(0, limit)
}
